<template>
    <UApp :locale="uiLocale">
        <UTheme :props="uiDefaults">
            <component :is="layout">
                <router-view />
            </component>
        </UTheme>
    </UApp>
</template>

<script lang="ts" setup>
    import * as locales from '@nuxt/ui/locale'
    import { computed, onMounted } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute } from 'vue-router'

    import Blank    from '@/layouts/Blank.vue'
    import Default  from '@/layouts/Default.vue'
    import Document from '@/layouts/Document.vue'

    import { useModuleRefresh } from '@/libs/moduleRefresh'
    import { usePointerGuard } from '@/libs/pointerGuard'
    import { uiDefaults } from '@/libs/uiDefaults'
    import { useAppStore } from '@/stores/app'

    const route = useRoute()
    const { locale } = useI18n()
    const appStore   = useAppStore()

    // Keeps Nuxt UI's built-in component texts in sync with the vue-i18n locale.
    const uiLocale = computed(() => (locales as Record<string, typeof locales.en>)[locale.value] ?? locales.en)

    const layouts = { Default, Blank, Document }
    const layout  = computed(() => layouts[(route.meta.layout as keyof typeof layouts) ?? 'Default'])

    // Keeps the licence current in a tab that is never reloaded: a module bought — or
    // withdrawn — this afternoon must not wait for the next sign-in to be noticed.
    // Registered here rather than in onMounted because it needs the router of this
    // setup scope, and it holds nothing a page load has not already asked for.
    useModuleRefresh()

    // A reload does not go through the login screen, so the signed-in account is read
    // here as well: without it the app would know it has a token and nothing about
    // whose it is — including which language this person reads in.
    //
    // Through ensureUserData rather than loadUserData because the router's guard wants
    // the same answer at the same moment — it may not decide a module question before
    // the licence is in — and one read serves both.
    onMounted(() => {
        void appStore.ensureUserData()

        // Watches for the click lock a closed dialog can leave on the page.
        usePointerGuard()
    })
</script>
